import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain,
  MessageSquare,
  Eye,
  BarChart3,
  Rocket,
  Zap,
  ArrowRight,
  Check,
  Search,
  FileText,
  Database,
  Code2,
  Settings,
  Cloud,
  GitBranch,
  Activity,
  Shield,
  Users,
  Clock,
  Target,
  Star,
  TrendingUp,
  FileSearch,
  Factory,
  Stethoscope,
  ChevronRight,
  X,
  Send,
  Loader,
  Briefcase
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= PROJECT INQUIRY MODAL =============
const ProjectInquiryModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: selectedPlan || '',
    timeline: '',
    description: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update budget when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, budget: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.projectType) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'AI Project Execution',
          message: `AI Project Inquiry\n\nCompany: ${formData.company}\nProject Type: ${formData.projectType}\nBudget Tier: ${formData.budget || 'Not specified'}\nTimeline: ${formData.timeline || 'Not specified'}\n\nProject Description:\n${formData.description || 'No description provided'}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Inquiry submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', projectType: '', budget: '', timeline: '', description: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Inquiry error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', projectType: '', budget: selectedPlan || '', timeline: '', description: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Inquiry Received!</h3>
            <p className="text-gray-400 mb-6">
              Our project team will review your requirements and schedule a discovery call within 24 hours.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Rocket size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">End-to-End Delivery</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Start Your Project</h2>
            <p className="text-teal-400 mb-6">Let's build production-ready AI together</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Project Type *</label>
                <select
                  value={formData.projectType}
                  onChange={(e) => handleChange('projectType', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select project type</option>
                  <option value="Custom AI Development" className="bg-slate-950">Custom AI Development</option>
                  <option value="NLP / Chatbot Solution" className="bg-slate-950">NLP / Chatbot Solution</option>
                  <option value="Computer Vision" className="bg-slate-950">Computer Vision</option>
                  <option value="Predictive Analytics" className="bg-slate-950">Predictive Analytics</option>
                  <option value="MLOps Pipeline" className="bg-slate-950">MLOps Pipeline</option>
                  <option value="AI Integration" className="bg-slate-950">AI Integration</option>
                  <option value="Multiple / Not Sure" className="bg-slate-950">Multiple / Not Sure</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Budget Tier</label>
                  <select
                    value={formData.budget}
                    onChange={(e) => handleChange('budget', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="POC ($15K)" className="bg-slate-950">POC ($15K)</option>
                    <option value="MVP ($45K)" className="bg-slate-950">MVP ($45K)</option>
                    <option value="Enterprise ($150K+)" className="bg-slate-950">Enterprise ($150K+)</option>
                    <option value="Custom Budget" className="bg-slate-950">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Timeline</label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="ASAP" className="bg-slate-950">ASAP</option>
                    <option value="1-2 months" className="bg-slate-950">1-2 months</option>
                    <option value="3-6 months" className="bg-slate-950">3-6 months</option>
                    <option value="6+ months" className="bg-slate-950">6+ months</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Project Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe your AI project goals and challenges..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Inquiry
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onStartProject }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#methodology" className="text-sm text-gray-300 hover:text-white transition">Process</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Start Project
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onStartProject }) => {
  const stats = [
    { value: '150+', label: 'Projects Delivered' },
    { value: '95%', label: 'On-Time Delivery' },
    { value: '4.9/5', label: 'Client Satisfaction' },
    { value: '100%', label: 'Production Success' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['End-to-End Delivery', 'Production Ready', 'Guaranteed Results'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">AI Project</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Execution
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Full-Stack AI Solutions From Concept to Production
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Transform your AI vision into production-grade reality. We handle everything—from 
          architecture design and model development to deployment and monitoring. Fixed timeline, 
          fixed budget, measurable outcomes. Your AI project, executed flawlessly.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Start Your Project <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            View Case Studies
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: Brain,
      title: 'Custom AI Development',
      description: 'Build bespoke AI models tailored to your specific business challenges. From predictive analytics to computer vision, we engineer solutions that deliver ROI.',
      features: [
        'Custom model architecture & training',
        'Transfer learning & fine-tuning',
        'Hyperparameter optimization & tuning',
        'Model evaluation & validation',
        'Performance benchmarking & reporting'
      ]
    },
    {
      icon: MessageSquare,
      title: 'NLP Solutions',
      description: 'Deploy intelligent language systems—chatbots, document processing, sentiment analysis, and LLM-powered applications for real-world business automation.',
      features: [
        'Conversational AI & chatbot development',
        'Document intelligence & extraction',
        'Sentiment analysis & text classification',
        'LLM integration & RAG systems',
        'Multi-language support & translation'
      ]
    },
    {
      icon: Eye,
      title: 'Computer Vision',
      description: 'Advanced vision systems for object detection, facial recognition, quality control, and automated visual inspection across industries.',
      features: [
        'Object detection & classification',
        'Facial recognition & biometric systems',
        'Image segmentation & analysis',
        'Video processing & real-time tracking',
        'OCR & document digitization'
      ]
    },
    {
      icon: BarChart3,
      title: 'Predictive Analytics',
      description: 'Forecast trends, predict outcomes, and optimize decisions with machine learning models built on your historical data and business intelligence.',
      features: [
        'Time series forecasting & demand prediction',
        'Customer churn & retention modeling',
        'Risk assessment & fraud detection',
        'Sales forecasting & revenue optimization',
        'Anomaly detection & monitoring systems'
      ]
    },
    {
      icon: Rocket,
      title: 'MLOps Pipeline',
      description: 'Production-grade ML infrastructure with automated training, deployment, monitoring, and continuous improvement for sustainable AI operations.',
      features: [
        'CI/CD pipelines for ML workflows',
        'Model versioning & experiment tracking',
        'Automated retraining & deployment',
        'Performance monitoring & alerting',
        'A/B testing & model comparison'
      ]
    },
    {
      icon: Zap,
      title: 'AI Integration',
      description: 'Seamlessly integrate AI capabilities into existing systems, applications, and workflows with minimal disruption and maximum impact.',
      features: [
        'API development & microservices',
        'Legacy system integration',
        'Cloud deployment (AWS, GCP, Azure)',
        'Scalable architecture design',
        'Security & compliance implementation'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Service Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Execution Services
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            Comprehensive AI project delivery across the entire machine learning lifecycle. 
            From proof-of-concept to enterprise-scale deployment, we build AI that works.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= METHODOLOGY SECTION =============
const MethodologySection = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery Phase',
      description: 'Deep dive into your business objectives, data landscape, technical constraints, and success metrics. We validate feasibility and define clear project scope.',
      deliverables: 'Technical specification, data audit, feasibility report, project roadmap'
    },
    {
      number: '02',
      title: 'Data Engineering',
      description: 'Build robust data pipelines, clean and preprocess datasets, engineer features, and establish data quality frameworks for model training.',
      deliverables: 'Data pipeline, cleaned datasets, feature store, EDA reports'
    },
    {
      number: '03',
      title: 'Model Development',
      description: 'Experiment with multiple algorithms, train and optimize models, validate performance, and select the best architecture for production deployment.',
      deliverables: 'Trained models, performance metrics, experiment logs, model cards'
    },
    {
      number: '04',
      title: 'Integration Build',
      description: 'Develop APIs, build user interfaces, integrate with existing systems, and create the complete application layer around your AI models.',
      deliverables: 'REST APIs, web/mobile interfaces, system integrations, documentation'
    },
    {
      number: '05',
      title: 'Deployment Launch',
      description: 'Deploy to production infrastructure with monitoring, logging, and alerting. Implement security, scalability, and disaster recovery protocols.',
      deliverables: 'Production deployment, monitoring dashboards, runbooks, SLA docs'
    },
    {
      number: '06',
      title: 'Optimization Support',
      description: 'Monitor performance, gather feedback, retrain models, and continuously improve accuracy and efficiency with ongoing optimization cycles.',
      deliverables: 'Performance reports, model updates, optimization recommendations'
    }
  ];

  return (
    <section id="methodology" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Execution Framework
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Project Methodology
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Our battle-tested 6-phase approach ensures predictable outcomes, transparent progress, 
            and production-ready AI systems that deliver measurable business value.
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal-500/50 via-blue-500/50 to-emerald-500/50 hidden md:block" />

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <motion.div
                    className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <h3 className="text-xl font-bold text-teal-400 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {step.description}
                    </p>
                    <p className="text-sm text-emerald-400">
                      <span className="font-semibold">📋 Deliverables:</span> {step.deliverables}
                    </p>
                  </motion.div>
                </div>

                <div className="relative z-10">
                  <motion.div
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-teal-500 to-emerald-500 flex items-center justify-center text-slate-950 text-2xl font-bold shadow-lg shadow-teal-500/30"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {step.number}
                  </motion.div>
                </div>

                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============= TECH STACK SECTION =============
const TechStackSection = () => {
  const categories = [
    {
      title: 'ML Frameworks',
      items: ['TensorFlow', 'PyTorch', 'Keras', 'Scikit-learn', 'XGBoost', 'LightGBM', 'JAX', 'FastAI']
    },
    {
      title: 'NLP & LLMs',
      items: ['Hugging Face', 'LangChain', 'LlamaIndex', 'OpenAI API', 'Anthropic Claude', 'spaCy', 'NLTK', 'Transformers']
    },
    {
      title: 'Computer Vision',
      items: ['OpenCV', 'YOLO', 'Detectron2', 'Pillow', 'Albumentations', 'MMDetection', 'TorchVision']
    },
    {
      title: 'Data Engineering',
      items: ['Apache Spark', 'Apache Airflow', 'Pandas', 'NumPy', 'Dask', 'DVC', 'Great Expectations']
    },
    {
      title: 'MLOps & Deployment',
      items: ['Docker', 'Kubernetes', 'MLflow', 'Kubeflow', 'TensorFlow Serving', 'FastAPI', 'Weights & Biases', 'Ray']
    },
    {
      title: 'Cloud Platforms',
      items: ['AWS SageMaker', 'GCP Vertex AI', 'Azure ML', 'AWS Lambda', 'GCP Cloud Run', 'EC2/GCE']
    }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Technology Stack
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Engineering Arsenal
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            We leverage cutting-edge AI frameworks, cloud platforms, and development tools 
            to build robust, scalable, and production-ready AI solutions.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="space-y-10">
            {categories.map((category, catIndex) => (
              <div key={catIndex}>
                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-4">
                  {category.title}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {category.items.map((item, itemIndex) => (
                    <motion.span
                      key={itemIndex}
                      className="px-4 py-2 bg-white/[0.03] border border-white/10 rounded-full text-sm font-medium text-teal-400 hover:border-teal-500/50 hover:bg-teal-500/10 transition-all cursor-default"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.03 }}
                      viewport={{ once: true }}
                      whileHover={{ y: -3 }}
                    >
                      {item}
                    </motion.span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Proof of Concept',
      price: '$15K',
      period: '4-6 Week Sprint',
      featured: false,
      budgetValue: 'POC ($15K)',
      features: [
        'Single use case validation',
        'Model prototyping & testing',
        'Feasibility assessment',
        'Technical documentation',
        'Presentation & recommendations',
        'Source code delivery'
      ]
    },
    {
      tier: 'MVP Development',
      price: '$45K',
      period: '8-12 Week Project',
      featured: true,
      budgetValue: 'MVP ($45K)',
      features: [
        'End-to-end AI solution',
        'Production-ready models',
        'API development & integration',
        'Basic UI/dashboard',
        'Cloud deployment',
        'Performance monitoring',
        '30 days post-launch support',
        'Complete documentation'
      ]
    },
    {
      tier: 'Enterprise Solution',
      price: '$150K+',
      period: '3-6 Month Engagement',
      featured: false,
      budgetValue: 'Enterprise ($150K+)',
      features: [
        'Multi-model AI platform',
        'Advanced MLOps infrastructure',
        'Custom integrations',
        'Scalable architecture',
        'Security & compliance',
        'Team training & handoff',
        '6 months support & optimization',
        'SLA guarantees'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Investment Models
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Project Pricing
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Flexible engagement models designed for projects of any scale. Fixed-price for 
            predictability or time-and-materials for evolving requirements.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.budgetValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PORTFOLIO SECTION =============
const PortfolioSection = () => {
  const projects = [
    {
      icon: Stethoscope,
      title: 'Medical Diagnosis AI',
      description: 'Built computer vision system for automated medical image analysis, achieving 94% accuracy in detecting anomalies. Reduced diagnosis time by 60%.',
      tags: ['Computer Vision', 'TensorFlow', 'Healthcare']
    },
    {
      icon: MessageSquare,
      title: 'Customer Support Bot',
      description: 'Deployed intelligent chatbot handling 10K+ queries daily with 89% resolution rate. Reduced support costs by $500K annually.',
      tags: ['NLP', 'LangChain', 'E-commerce']
    },
    {
      icon: TrendingUp,
      title: 'Demand Forecasting',
      description: 'Predictive analytics system forecasting demand across 500+ SKUs with 92% accuracy. Optimized inventory and reduced waste by 35%.',
      tags: ['Time Series', 'XGBoost', 'Retail']
    },
    {
      icon: Search,
      title: 'Fraud Detection System',
      description: 'Real-time fraud detection processing 1M+ transactions daily. Identified $2M in fraudulent activity in first quarter with 98% precision.',
      tags: ['Anomaly Detection', 'PyTorch', 'Fintech']
    },
    {
      icon: FileText,
      title: 'Document Intelligence',
      description: 'Automated document processing system extracting data from 50K+ documents monthly. Reduced manual processing time by 80%.',
      tags: ['OCR', 'NLP', 'Legal Tech']
    },
    {
      icon: Factory,
      title: 'Quality Control Vision',
      description: 'Computer vision system for automated defect detection on production lines. Improved quality control accuracy to 99.5% with real-time processing.',
      tags: ['Computer Vision', 'YOLO', 'Manufacturing']
    }
  ];

  return (
    <section id="portfolio" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Case Studies
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Project Portfolio
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Real projects, real results. See how we've transformed business challenges into 
            production AI systems that drive measurable outcomes.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-teal-500/30 transition-all duration-300 h-full">
                <div className="h-48 bg-gradient-to-br from-blue-500/20 via-teal-500/20 to-emerald-500/20 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(20,184,166,0.3),transparent_70%)]" />
                  <project.icon className="w-20 h-20 text-teal-400/80 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 mb-4 leading-relaxed text-sm">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs text-emerald-400"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onStartProject }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Launch Your AI Project
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            From concept to production in weeks, not months. Let's build AI that delivers real business value.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Start Project <ArrowRight size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              End-to-end AI project execution from concept to production.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/custom-ai-ml-solutions" className="hover:text-teal-400 transition">Custom Solutions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIProjectExecutionPage() {
  const [inquiryModal, setInquiryModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleStartProject = (plan = null) => {
    setSelectedPlan(plan);
    setInquiryModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onStartProject={() => handleStartProject()} />
      <Hero onStartProject={() => handleStartProject()} />
      <ServicesSection />
      <MethodologySection />
      <TechStackSection />
      <PricingSection onSelectPlan={handleStartProject} />
      <PortfolioSection />
      <FinalCTA onStartProject={() => handleStartProject()} />
      <Footer />

      {/* Project Inquiry Modal */}
      <ProjectInquiryModal
        isOpen={inquiryModal}
        onClose={() => setInquiryModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}